import { Modal } from "@mui/material";
import React, { useState } from "react";
import { useRecoilState } from "recoil";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import {
  DraftInfoModalWrapper,
  DraftInfoModalMessage,
  MissingInfoModalTitle,
  DraftModalButtons,
} from "../Grid/Grid.styles";
import { NewRequisitionForm } from "../../recoil/types";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import {
  deleteDraftRequisitionOrder,
  submitDraftRequisitionOrder,
} from "./Services/NewReqService";
import { convertNewReqFormToRequest } from "./Services/helpers";
import { RefreshNewRequisitionOrders } from "../Grid/Services/GridService";
import {
  currentEditDraftRequisition,
  currentNewRequisition,
  orderedRequisitions,
} from "../../recoil/atoms";
import { getInitialNewRequisitionForm } from "../../recoil/helpers";
import { DRAFT_STATUS } from "./types";
import { formatDateTimeFromEpoch } from "../../utils/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

export const NewReqDraftActionModal: React.FC<{
  type: "saved" | "deleted" | "delete" | "unsaved";
  newReqForm: NewRequisitionForm;
  setDraft: React.Dispatch<
    React.SetStateAction<"saved" | "deleted" | "delete" | "unsaved" | undefined>
  >;
  setNewReqOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ type, newReqForm, setDraft, setNewReqOpen }) => {
  const { deviceType } = useDeviceHook();
  const { enabled: shipKitEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ShipKitToPatient,
  );

  const { auth, setLoggedOut } = useAuthenticationHook();
  const [, setAllOrderedRequisitions] = useRecoilState(orderedRequisitions);
  const [, setGlobalNewRequisition] = useRecoilState(currentNewRequisition);
  const [draftEditReq, setDraftEditReq] = useRecoilState(
    currentEditDraftRequisition,
  );
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (auth && newReqForm.unityCompleteId) {
      await deleteDraftRequisitionOrder(
        newReqForm.unityCompleteId,
        auth?.token,
        setLoggedOut,
      );
      await RefreshNewRequisitionOrders(
        auth.token,
        setAllOrderedRequisitions,
        setLoggedOut,
      );
      setDraft("deleted");
      setNewReqOpen(false);
    }
  };

  const getTitle = () => {
    if (type === "saved") return "DRAFT SAVED";
    if (type === "deleted") return "SUCCESS";
    if (type === "delete") return "DELETE DRAFT";
    if (type === "unsaved") return "DO YOU WANT TO KEEP UNSAVED CHANGES?";
  };

  const getText = () => {
    if (type === "saved") return "Your draft has been saved successfully!";
    if (type === "deleted") return "Your draft has been deleted.";
    if (type === "delete")
      return `Do you want to delete the draft that was saved for ${
        newReqForm.firstName
      } ${newReqForm.lastName} at ${formatDateTimeFromEpoch(
        newReqForm.createdAt,
      )}?`;
    if (type === "unsaved")
      return "Your inputs will be lost if you don’t save them as drafts.";
  };

  const getButtons = () => {
    if (type === "saved") {
      return (
        <Button
          testId="BackButtonTest"
          style={{
            flex: 1,
          }}
          label="OK"
          reverse
          backgroundColor="white"
          borderColor="neutral500"
          size="small"
          onClick={() => {
            setDraft(undefined);
            setNewReqOpen(false);
            setGlobalNewRequisition(getInitialNewRequisitionForm());
          }}
        />
      );
    }
    if (type === "deleted") {
      return (
        <Button
          testId="BackButtonTest"
          style={{
            flex: 1,
          }}
          label="OK"
          reverse
          backgroundColor="white"
          borderColor="neutral500"
          size="small"
          onClick={() => {
            setDraft(undefined);
            setNewReqOpen(false);
            setGlobalNewRequisition(getInitialNewRequisitionForm());
          }}
        />
      );
    }
    if (type === "delete") {
      return (
        <>
          <Button
            testId="BackButtonTest"
            style={{
              flex: 1,
            }}
            label="CANCEL"
            reverse
            backgroundColor="white"
            borderColor="neutral500"
            size="small"
            onClick={() => {
              setDraft(undefined);
              if (!draftEditReq) {
                setNewReqOpen(false);
                setGlobalNewRequisition(getInitialNewRequisitionForm());
              }
            }}
          />
          <Button
            testId="BackButtonTest"
            style={{
              flex: 1,
            }}
            label="DELETE"
            reverse
            backgroundColor="white"
            borderColor="neutral500"
            size="small"
            onClick={() => handleDelete()}
          />
        </>
      );
    }
    if (type === "unsaved") {
      return (
        <>
          <Button
            testId="BackButtonTest"
            style={{
              flex: 1,
            }}
            label="DISCARD CHANGES"
            reverse
            backgroundColor="white"
            borderColor="neutral500"
            size="small"
            onClick={() => {
              setDraft(undefined);
              setNewReqOpen(false);
              setDraftEditReq(undefined);
              setGlobalNewRequisition(getInitialNewRequisitionForm());
            }}
          />
          {newReqForm.firstName &&
            newReqForm.lastName &&
            newReqForm.dateOfBirth &&
            (loading ? (
              <SpinnerLoading />
            ) : (
              <Button
                label="SAVE AS DRAFT"
                reverse
                backgroundColor="white"
                borderColor="neutral500"
                size="small"
                onClick={async () => {
                  if (auth) {
                    setLoading(true);
                    await submitDraftRequisitionOrder(
                      {
                        ...convertNewReqFormToRequest(
                          newReqForm,
                          shipKitEnabled,
                        ),
                        draft_status:
                          draftEditReq?.draft_status ===
                          DRAFT_STATUS.REDRAW_DRAFT
                            ? DRAFT_STATUS.REDRAW_DRAFT
                            : DRAFT_STATUS.DRAFT,
                      },
                      auth?.token,
                      setLoggedOut,
                    );
                    await RefreshNewRequisitionOrders(
                      auth.token,
                      setAllOrderedRequisitions,
                      setLoggedOut,
                    );
                    setLoading(false);
                    setDraft("saved");
                    setDraftEditReq(undefined);
                    setNewReqOpen(false);
                  }
                }}
              />
            ))}
        </>
      );
    }
  };

  return (
    <Modal
      open
      onClose={() => undefined}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <DraftInfoModalWrapper deviceType={deviceType}>
        <MissingInfoModalTitle deviceType={deviceType}>
          {getTitle()}
        </MissingInfoModalTitle>
        <DraftInfoModalMessage>{getText()}</DraftInfoModalMessage>
        <DraftModalButtons>{getButtons()}</DraftModalButtons>
      </DraftInfoModalWrapper>
    </Modal>
  );
};
